import { check } from './core.ts';
import { mapIter } from './iterators.ts';
import type { Scalar, Time } from './units.ts';
import { Milliseconds } from './units.ts';

export interface LruOptions {
  maxCount?: number;
  maxAge?: Scalar<Time>;
}

interface LruNode<K, V> {
  key: K;
  value: V;
  timestamp: Date;
  newer: LruNode<K, V>;
  older: LruNode<K, V>;
}

function unlink<K, V>(node: LruNode<K, V>) {
  node.older.newer = node.newer;
  node.newer.older = node.older;
}

export class Lru<K, V> {
  constructor(readonly options?: LruOptions) {
    this.newest = {} as LruNode<K, V>;
    this.oldest = {} as LruNode<K, V>;
    this.newest.older = this.oldest;
    this.oldest.newer = this.newest;
  }

  set(key: K, value: V) {
    let cacheAtSizeLimit = false;
    const timestamp = new Date();
    let node = this.index.get(key);
    if (node) {
      unlink(node);
      node.value = value;
      node.timestamp = timestamp;
      node.newer = this.newest;
      node.older = this.newest.older;
    } else {
      const maxSize = this.options?.maxCount ?? Number.MAX_SAFE_INTEGER;
      while (this.index.size >= maxSize) {
        const oldest = this.oldest.newer;
        check(this.index.delete(oldest.key));
        unlink(oldest);
        cacheAtSizeLimit = true;
      }
      node = { key, value, timestamp, newer: this.newest, older: this.newest.older };
      this.index.set(key, node);
    }
    node.older.newer = node;
    node.newer.older = node;
    return cacheAtSizeLimit;
  }

  get<_U>(key: K): V | undefined;
  get<U>(key: K, valueIfAbsent: U): V | U;
  get<U>(key: K, valueIfAbsent?: U): V | U {
    const timestamp = new Date();
    if (this.options?.maxAge) {
      const minTimestamp = new Date(timestamp.getTime() - this.options.maxAge.value(Milliseconds));
      while (this.index.size) {
        const oldest = this.oldest.newer;
        // console.log(oldest.timestamp, minTimestamp);
        if (oldest.timestamp >= minTimestamp) {
          break;
        }
        check(this.index.delete(oldest.key));
        unlink(oldest);
      }
    }
    const node = this.index.get(key);
    if (node) {
      unlink(node);
      node.timestamp = timestamp;
      node.newer = this.newest;
      node.older = this.newest.older;
      node.older.newer = node;
      node.newer.older = node;
      return node.value;
    }
    return valueIfAbsent as U;
  }

  delete(key: K) {
    const node = this.index.get(key);
    if (node) {
      this.index.delete(key);
      unlink(node);
    }
  }

  clear() {
    this.index.clear();
    this.newest.older = this.oldest;
    this.oldest.newer = this.newest;
  }

  keys() {
    return this.index.keys();
  }

  values() {
    return mapIter(this.index.values(), (n) => n.value);
  }

  entries() {
    return mapIter(this.index.entries(), ([k, n]) => [k, n.value] as const);
  }

  private readonly index = new Map<K, LruNode<K, V>>();
  private readonly newest: LruNode<K, V>;
  private readonly oldest: LruNode<K, V>;
}
